<!--
  Created 21.12.2022
  Originated from Tailwind UI component: Lists, Grid lists, Simple Cards
  https://tailwindui.com/components/application-ui/lists/grid-lists#component-4ec337def61b547035ce908c128bca5c
-->
<template>
  <div>
    <ul
      role="list"
      class="mt-3 grid grid-cols-1 gap-3">
      <li
        v-for="(item, index) in itemsComputed"
        :key="'cardItem-' + index + '--' + (item.id ?? uniqueId())"
        class="col-span-1 flex rounded-md shadow-sm"
        :class="{'opacity-50': item.visible === false}">
        <div :class="[cardState[item.status].bgColor, cardState[item.status].textColor, 'flex w-16 shrink-0 items-center justify-center rounded-l-md text-sm font-medium']">
          {{ cardState[item.status].initials }}
        </div>
        <div class="flex min-w-0 flex-1 items-center justify-between rounded-r-md border-y border-r border-gray-200 bg-white text-gray-900 hover:bg-gray-100 hover:text-indigo-700">
          <div class="min-w-0 flex-1 px-4 py-2 text-sm">
            <Link :href="route(routeName, {id: item.id})">
              <p class="truncate font-medium">
                {{ item.title ?? item.name }}
              </p>
              <div v-if="item.places">
                <p class="text-gray-500">
                  <span
                    v-for="(place, placeIndex) in item.places"
                    :key="placeIndex">
                    <span v-if="placeIndex !== 0">,</span> <span>{{ place.name }}</span>
                  </span>
                  <span> - {{ item.currency_id }} {{ item.amount_pr_pax }} per pax ({{ item.pax }} pax)</span>
                </p>
              </div>
              <div v-else-if="item.start_date || item.total_travelers">
                <p class="text-gray-500">
                  <span
                    v-if="item.start_date"
                    class="mr-1">Starts {{ getDateTimeInDestinationTimezone(item.start_date, item.data.destination.timezone, 'DD.MM.YYYY') }}</span>
                  <span v-if="item.total_travelers">with {{ item.total_travelers }} pax &times; {{ formatCurrency(item.budget_per_traveller) }} p.p.</span>
                </p>
              </div>
              <div v-else>
                <p class="text-gray-500">
                  {{ item.subtitle || '&nbsp;' }}
                </p>
              </div>
            </Link>
          </div>

          <!-- Start: Options menu -->
          <div
            v-if="settingsMenuComputed.length > 0"
            class="relative flex shrink-0 items-center space-x-2 pr-2">
            <!-- Eye icon with tooltip-->
            <Tooltip>
              <template #trigger>
                <EyeSlashIcon
                  v-if="!item.visible"
                  class="size-5 text-gray-400" />
              </template>
              <template #content>
                <p class="text-sm">
                  This item is not listed anywhere else in TravelOffice
                </p>
              </template>
            </Tooltip>
            <!-- Options button -->
            <button
              type="button"
              class="inline-flex size-8 items-center justify-center rounded-full bg-transparent text-gray-400
                hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              :class="{ '!bg-gray-400 !text-gray-600': showOptionsMap[item.id ?? index] }"
              @click.stop="toggleOptions(item.id ?? index)">
              <span class="sr-only">Open options</span>
              <EllipsisVerticalIcon
                class="size-5"
                aria-hidden="true" />
            </button>
            <!-- Dropdown menu, show/hide based on menu open state. -->
            <div
              v-if="showOptionsMap[item.id ?? index]"
              v-click-outside="closeOptions"
              class="absolute bottom-[-0.1rem] left-[-10.8rem] z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/50 focus:outline-none">
              <p
                v-for="(menuItem, menuIndex) in settingsMenuComputed"
                :key="'CardSetting-' + menuIndex + '--' + (menuIndex.id ?? uniqueId())"
                class="block cursor-pointer px-4 py-2 text-sm text-gray-700 first-of-type:rounded-t-md last-of-type:rounded-b-md hover:bg-gray-100 hover:text-gray-900"
                role="menuitem"
                @click="$emit('SettingsMenuClick', {
                  action: (Array.isArray(menuItem.action) ? menuItem.action[item[menuItem.dynamicProperty] ? 0 : 1] : menuItem.action),
                  id: menuItem.id ?? item.id ?? index
                })">
                {{ Array.isArray(menuItem.text) ? menuItem.text[item[menuItem.dynamicProperty] ? 0 : 1] : menuItem.text }}
              </p>
            </div>
          </div>
          <!-- End: Options menu -->
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import {
  EllipsisVerticalIcon, EyeSlashIcon,
} from '@heroicons/vue/24/outline';
import { Link } from '@inertiajs/vue3';
import {
  formatCurrency, getDateTimeInDestinationTimezone, uniqueId,
} from '@/utils';
import {
  computed, ref,
} from 'vue';
import Tooltip from '@/Components/Tooltip.vue';

const props = defineProps({
  /**
   * The items to display as cards in the list
   *
   * @example [
   *   { id: 1, title: 'Proposal 1', visible: true, status: 'draft', places: [{ name: 'Place 1' }, { name: 'Place 2' }], currency_id: 'NOK', amount_pr_pax: 100, pax: 10},
   *   { id: 2, title: 'Just some title', subtitle: 'With a subtitle present, visible: false, status: 'pending'},
   * ]
   */
  items: {
    type: Array,
    required: true,
    default: () => [],
  },
  /**
   * Provide the route name to generate the URL for the item to be clicked
   *
   * @example proposals.show
   */
  routeName: {
    type: String,
    required: false,
    default: '',
  },
  /**
   * Provide settings menu items for the card - Action and ID emitted when clicked.
   * Properties text and action supports arrays to dynamically generate the menu items.
   * Property dynamicProperty is used to determine which array index to use.
   *
   * @example [
   *   {
   *      text: 'View proposal',
   *      action: 'proposals.show',
   *      id: 1, // optional - uses ID of items array if not provided here
   *   },
   *   {
   *     text: ['Hide proposal', 'Show proposal'],
   *     action: ['hide', 'show'],
   *     dynamicProperty: 'visible',
   *   }
   * ]
   */
  settingsMenu: {
    type: Array,
    required: false,
    default: () => [],
  },
});

const itemsComputed = computed(() => props.items);
const settingsMenuComputed = computed(() => props.settingsMenu);

const cardState = {
  draft: {
    bgColor: 'bg-gray-200', textColor: 'text-gray-800', initials: 'DRA',
  },
  pending: {
    bgColor: 'bg-yellow-200', textColor: 'text-yellow-800', initials: 'PND',
  },
  accepted: {
    bgColor: 'bg-green-200', textColor: 'text-green-800', initials: 'ACP',
  },
  archived: {
    bgColor: 'bg-green-200', textColor: 'text-green-800', initials: 'ARC',
  },
};

const showOptionsMap = ref({});
showOptionsMap.value = {};

/**
 * Function to toggle the options menu for a given item
 * @param {string|number} itemKey - The key of the item to toggle
 */
function toggleOptions(itemKey) {
  // Convert itemKey to a string
  const key = String(itemKey);
  // Close all other options menus
  Object.keys(showOptionsMap.value).forEach((k) => {
    if (k !== key) {
      showOptionsMap.value[k] = false;
    }
  });
  // Toggle the clicked item's options menu
  showOptionsMap.value[key] = !showOptionsMap.value[key];
}

/**
 * Function to close all the options menus
 */
function closeOptions() {
  // Close the options menu for all items
  Object.keys(showOptionsMap.value).forEach((key) => {
    showOptionsMap.value[key] = false;
  });
}

defineEmits(['SettingsMenuClick']);

</script>
